import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useMeinTagNachtragen } from './api/meintag';
import { useAbhakenNachtragen } from './api/woche';
import { AppHuelle } from './layout/AppHuelle';
import { TimerWaechter } from './kochen/TimerWaechter';
import { useAuth } from './lib/auth';
import { BILD_SITZUNG_TAKT_MS, bildSitzungErneuern } from './lib/bildSitzung';
import { Anmelden } from './seiten/Anmelden';
import { Start } from './seiten/Start';
import { KnopfLink, Laedt, LeerZustand } from './ui';

/**
 * Phase 6: jede Seite ausser Start und Anmelden (die ersten Ansichten) ist ein eigener Chunk. Der Service Worker
 * legt alle Chunks beim Installieren in den Precache, offline fehlt also keiner.
 */
function seite<K extends string>(laden: () => Promise<Record<K, ComponentType>>, name: K) {
  return lazy(() => laden().then((m) => ({ default: m[name] })));
}
const Nora = seite(() => import('./seiten/Nora'), 'Nora');
const NoraWissen = seite(() => import('./seiten/NoraWissen'), 'NoraWissen');
const Profil = seite(() => import('./seiten/Profil'), 'Profil');
const Entdecken = seite(() => import('./seiten/Entdecken'), 'Entdecken');
const Info = seite(() => import('./seiten/Info'), 'Info');
const ImportSeite = seite(() => import('./seiten/Import'), 'ImportSeite');
const ImportQuelleSeite = seite(() => import('./seiten/Import'), 'ImportQuelleSeite');
const ImportGespeichertSeite = seite(() => import('./seiten/Import'), 'ImportGespeichertSeite');
const MeineRezepte = seite(() => import('./seiten/MeineRezepte'), 'MeineRezepte');
const KollektionSeite = seite(() => import('./seiten/MeineRezepte'), 'KollektionSeite');
const RezeptSeite = seite(() => import('./seiten/Rezept'), 'RezeptSeite');
const KochmodusSeite = seite(() => import('./seiten/Kochmodus'), 'KochmodusSeite');
const RezeptBearbeitenSeite = seite(() => import('./seiten/RezeptBearbeiten'), 'RezeptBearbeitenSeite');
const RezeptNeuSeite = seite(() => import('./seiten/RezeptBearbeiten'), 'RezeptNeuSeite');
const Einkaufen = seite(() => import('./seiten/Einkaufen'), 'Einkaufen');
const MeineWoche = seite(() => import('./seiten/MeineWoche'), 'MeineWoche');
// Mein Tag (0.10.0): Tab nur mit eingeschalteter Kalorien-Uebersicht, Eintragen-Screens als Vollbild
const MeinTag = seite(() => import('./seiten/MeinTag'), 'MeinTag');
const RezeptEintragenSeite = seite(() => import('./seiten/MeinTagEintragen'), 'RezeptEintragenSeite');
const PackungEintragenSeite = seite(() => import('./seiten/MeinTagEintragen'), 'PackungEintragenSeite');
const BarcodeEintragenSeite = seite(() => import('./seiten/MeinTagEintragen'), 'BarcodeEintragenSeite');
const BeschreibenEintragenSeite = seite(() => import('./seiten/MeinTagEintragen'), 'BeschreibenEintragenSeite');
const EinrichtenSeite = seite(() => import('./seiten/MeinTagEintragen'), 'EinrichtenSeite');
const ZielSeite = seite(() => import('./seiten/MeinTagEintragen'), 'ZielSeite');

/**
 * Routen. /rezept/:slug hat keine Tab-Leiste, sondern die Aktionsleiste des Rezepts.
 * /rezept/:slug/bearbeiten und /neues-rezept (W4) haben die Speichern-Leiste.
 * /rezept/:slug/kochen ist der Kochmodus (W8): Vollbild ohne Tab-Leiste und ohne Nora-Knopf.
 * /import, /import/:id, /import/:id/gespeichert (W5) haben den Import-Kopf. /nora ist der Chat (W6) mit eigenem Kopf und Eingabeleiste. Alles unter AppHuelle hat Tab-Leiste und Nora-Knopf.
 */
/** Offline Abgehaktes nachtragen, egal auf welcher Seite man gerade ist. */
function Nachtragen() {
  useAbhakenNachtragen();
  useMeinTagNachtragen();
  const { zustand } = useAuth();
  const angemeldet = zustand.status === 'angemeldet';
  // Bild-Cookie gilt 24 h; bei laenger offener App alle 6 h erneuern (GET /ich setzt es beim Start)
  useEffect(() => {
    if (!angemeldet) return;
    const takt = window.setInterval(() => void bildSitzungErneuern(), BILD_SITZUNG_TAKT_MS);
    return () => window.clearInterval(takt);
  }, [angemeldet]);
  return null;
}

export function App() {
  return (
    <Suspense fallback={<Laedt />}>
    <Nachtragen />
    <TimerWaechter />
    <Routes>
      <Route path="/anmelden" element={<Anmelden />} />
      <Route path="/nora" element={<Nora />} />
      <Route path="/rezept/:slug" element={<RezeptSeite />} />
      <Route path="/rezept/:slug/bearbeiten" element={<RezeptBearbeitenSeite />} />
      <Route path="/rezept/:slug/kochen" element={<KochmodusSeite />} />
      <Route path="/neues-rezept" element={<RezeptNeuSeite />} />
      <Route path="/import" element={<ImportQuelleSeite />} />
      <Route path="/import/:id" element={<ImportSeite />} />
      <Route path="/import/:id/gespeichert" element={<ImportGespeichertSeite />} />
      <Route path="/mein-tag/rezept" element={<RezeptEintragenSeite />} />
      <Route path="/mein-tag/packung" element={<PackungEintragenSeite />} />
      <Route path="/mein-tag/barcode" element={<BarcodeEintragenSeite />} />
      <Route path="/mein-tag/beschreiben" element={<BeschreibenEintragenSeite />} />
      <Route path="/mein-tag/einrichten" element={<EinrichtenSeite />} />
      <Route path="/mein-tag/ziel" element={<ZielSeite />} />
      <Route element={<AppHuelle />}>
        <Route index element={<Start />} />
        <Route path="entdecken" element={<Entdecken />} />
        <Route path="rezepte" element={<MeineRezepte />} />
        <Route path="rezepte/kollektion/:id" element={<KollektionSeite />} />
        <Route path="woche" element={<MeineWoche />} />
        <Route path="einkaufen" element={<Einkaufen />} />
        <Route path="mein-tag" element={<MeinTag />} />
        <Route path="profil" element={<Profil />} />
        <Route path="profil/info" element={<Info />} />
        <Route path="profil/nora" element={<NoraWissen />} />
        <Route
          path="*"
          element={
            <LeerZustand
              titel="Diese Seite gibt es nicht"
              text="Vielleicht hat sich ein Link verändert."
              icon="entdecken"
              aktionen={<KnopfLink an="/">Zur Startseite</KnopfLink>}
            />
          }
        />
      </Route>
    </Routes>
    </Suspense>
  );
}
